'use client';

import { AddressValidation } from '@/models/Basket';
import { MapPin, Pencil, HelpCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import AddressValidationHelpModal from './AddressValidationHelpModal';
import { useState } from 'react';

function SectionDivider() {
  return <hr className="border-hzc_beige_300" />;
}

export default function AddressDistrictMismatchModal({
  open,
  validation,
  loading,
  onSelectDistrict,
  onUpdateAddress,
  onClose,
}: {
  open: boolean;
  validation: AddressValidation | null;
  loading?: boolean;
  onSelectDistrict: (districtId: number) => void;
  onUpdateAddress: () => void;
  onClose: () => void;
}) {
  const [helpOpen, setHelpOpen] = useState(false);

  const nearestId = validation?.nearestDistrictId;
  const nearestTitle = validation?.nearestDistrictTitle || 'En yakın mahalle';
  const nearestCityTitle = validation?.nearestCityTitle || validation?.addressCityTitle;
  const suggested = validation?.suggestedDistricts || [];

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
      >
        <DialogContent className="max-w-lg w-[95%] p-0 overflow-hidden gap-0 max-h-[min(90dvh,100%)] flex flex-col sm:top-[50%] top-auto bottom-0 sm:bottom-auto translate-y-0 sm:translate-y-[-50%] rounded-t-2xl sm:rounded-lg">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Adres ve mahalle uyuşmuyor</DialogTitle>
            <DialogDescription>
              {validation?.message ||
                'Girilen adres ile seçilen teslimat bölgesi uyuşmuyor. Devam etmek için aşağıdaki seçeneklerden birini kullanın.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col overflow-y-auto overscroll-contain min-h-0 flex-1">
            <div className="px-6 py-4">
              <Button
                className="w-full h-12 rounded-full bg-hzc_purple_500 hover:bg-hzc_purple_500 justify-start gap-3"
                disabled={!nearestId || loading}
                onClick={() => nearestId && onSelectDistrict(nearestId)}
              >
                <MapPin className="h-5 w-5 shrink-0" />
                <span className="text-left">
                  <span className="block text-sm font-semibold">Otomatik mahalle seç</span>
                  <span className="block text-xs opacity-90 truncate">
                    {nearestTitle}
                    {nearestCityTitle && (
                      <span className="ml-2 text-xs opacity-75 truncate">[{nearestCityTitle}]</span>
                    )}
                  </span>
                </span>
              </Button>
            </div>

            <SectionDivider />

            <div className="px-6 py-4 space-y-2">
              <p className="text-sm font-medium text-hzc_beige_900">Önerilen mahalleler</p>
              {suggested.length === 0 ? (
                <p className="text-sm text-hzc_beige_700">Uygun mahalle önerisi bulunamadı.</p>
              ) : (
                suggested.map((district) => (
                  <Button
                    key={district.id}
                    variant="outline"
                    className="w-full h-auto min-h-11 py-2 justify-between rounded-xl border-hzc_beige_300"
                    disabled={loading}
                    onClick={() => onSelectDistrict(district.id)}
                  >
                    <span className="truncate text-left">
                      <span className="block">{district.title}</span>
                      {district.cityTitle && (
                        <span className="block text-xs text-hzc_beige_700">
                          {district.cityTitle}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-hzc_beige_700 shrink-0 ml-2">
                      ~{district.distanceMeters} m
                    </span>
                  </Button>
                ))
              )}
            </div>

            <SectionDivider />

            <div className="px-6 py-4">
              <Button
                variant="outline"
                className="w-full h-11 rounded-full border-hzc_beige_300 gap-2"
                disabled={loading}
                onClick={onUpdateAddress}
              >
                <Pencil className="h-4 w-4" />
                Adresi güncelle
              </Button>
            </div>

            <SectionDivider />

            <div className="px-6 py-4">
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 text-hzc_purple_500 font-semibold text-sm hover:underline"
                onClick={() => setHelpOpen(true)}
              >
                <HelpCircle className="h-4 w-4" />
                Bu kontrol neden yapılıyor?
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddressValidationHelpModal open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}
